import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Divider, Grid, TextField, Tooltip, Typography } from '@mui/material';
import { Person, Star } from '@mui/icons-material';
import DashboardCard from 'components/DashboardCard/DashboardCard';
import useRequest from 'hooks/useRequest';
import api from 'api';
import { useDebounce } from '@uidotdev/usehooks';
import objectFieldDiff from 'lib/objectFieldDiff';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';

async function sendUserDataRequest() {
    try {
        const result = await api.get('/user');
        return result.data;
    } catch (err) {
        return Promise.reject(err.response.data);
    }
}

const MyAccount = () => {
    const { data, loading, hasLoaded, error, refetch } = useRequest({ requestFunction: sendUserDataRequest });
    const [user, setUser] = useState(data?.user);
    const [clinics, setClinics] = useState([]);
    const [diffs, setDiffs] = useState([]);
    const [inputErrors, setInputErrors] = useState({});
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [verifyNewPassword, setVerifyNewPassword] = useState('');
    const [saving, setSaving] = useState(false);

    const debouncedUser = useDebounce(user, 200);
    const hasInputErrors = useMemo(() => {
        return Object.keys(inputErrors).some((key) => Boolean(inputErrors[key]));
    }, [inputErrors]);

    // Put the user data in local state after loading
    useEffect(() => {
        if (data) {
            setUser(data.user);
            setClinics(data.clinics);
        }
    }, [data, setUser, setClinics]);

    // Update our diffs of changed fields
    useEffect(() => {
        if (data?.user && debouncedUser) {
            setDiffs(objectFieldDiff(data.user, debouncedUser));
        }
    }, [debouncedUser, data?.user, setDiffs]);

    const handleChange = useCallback(
        (name) => (event) => {
            const value = event.target.value;

            setUser((currentState) => ({
                ...currentState,
                [name]: value,
            }));

            if (name === 'login_email' || name === 'login_name') {
                if (value && !value.trim()) {
                    setInputErrors((currentErrors) => {
                        return { ...currentErrors, [name]: 'Please enter a valid value' };
                    });
                } else {
                    setInputErrors((currentErrors) => {
                        return { ...currentErrors, [name]: false };
                    });
                }
            }
        },
        [setUser, setInputErrors]
    );

    const sendUpdateRequest = useCallback(async () => {
        if (!data?.user) {
            return false;
        }

        const payload = {
            password,
        };

        const diff = objectFieldDiff(data.user, debouncedUser);

        if (diff.includes('login_email')) {
            payload.email = debouncedUser.login_email;
        }

        if (diff.includes('login_name')) {
            payload.name = debouncedUser.login_name;
        }

        if (newPassword) {
            payload.new_password = newPassword;
        }

        setSaving(true);

        try {
            const result = await api.put('/user', payload);

            enqueueSnackbar(result.data.message, {
                variant: 'success',
                autoHideDuration: 2500,
            });

            setNewPassword('');
            setVerifyNewPassword('');
            setPassword('');
            setInputErrors({});

            await refetch();

            return true;
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || 'An error occurred while updating your profile', {
                variant: 'error',
                autoHideDuration: 2000,
            });

            const errors = err?.response?.data?.errors || {};
            const newInputErrors = {};

            if (errors.name) {
                newInputErrors.login_name = errors.name;
            }
            if (errors.email) {
                newInputErrors.login_email = errors.email;
            }
            if (errors.new_password) {
                newInputErrors.new_password = errors.new_password;
            }

            setInputErrors((current) => ({
                ...current,
                ...newInputErrors,
            }));

            return false;
        } finally {
            setSaving(false);
        }
    }, [
        data?.user,
        password,
        debouncedUser,
        newPassword,
        setPassword,
        setNewPassword,
        setVerifyNewPassword,
        setInputErrors,
        refetch,
    ]);

    const validateNewPassword = useCallback(
        (isVerificationField = false) =>
            () => {
                if (isVerificationField && (newPassword || verifyNewPassword) && newPassword !== verifyNewPassword) {
                    setInputErrors((currentErrors) => ({
                        ...currentErrors,
                        newPassword: 'New password and verification field must match',
                    }));
                }

                if (newPassword && verifyNewPassword && newPassword !== verifyNewPassword) {
                    setInputErrors((currentErrors) => ({
                        ...currentErrors,
                        newPassword: 'New password and verification field must match',
                    }));
                }

                if (newPassword === verifyNewPassword) {
                    setInputErrors((currentErrors) => ({
                        ...currentErrors,
                        newPassword: null,
                    }));
                }
            },
        [setInputErrors, newPassword, verifyNewPassword]
    );

    if (error) {
        return <div>Oops, something went wrong</div>;
    }

    if (!hasLoaded) {
        return <CircularProgress color="inherit" />;
    }

    console.log({ inputErrors });

    return (
        <DashboardCard
            color={'grey'}
            icon={<Person />}
            topContent="My Account"
            topLabel={user ? `Hi there, ${user.login_name}` : null}
            size="xl"
        >
            <Box sx={{ width: '100%' }}>
                {Boolean(user) && (
                    <Grid container spacing={0}>
                        <Grid size={{ xs: 4 }}>
                            <Grid container spacing={4}>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="h6">Your settings</Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        value={user.login_email}
                                        onChange={handleChange('login_email')}
                                        disabled={loading}
                                        label="Email Address"
                                    />
                                    {inputErrors.login_email ? <span>{inputErrors.login_email}</span> : null}
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        value={user.login_name}
                                        onChange={handleChange('login_name')}
                                        disabled={loading}
                                        label="Your Name"
                                    />
                                    {inputErrors.login_name ? <span>{inputErrors.login_name}</span> : null}
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Divider sx={{ marginBottom: '24px' }} />
                                    <Box>
                                        <TextField
                                            label="New password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            type="password"
                                            onBlur={validateNewPassword(false)}
                                        />
                                    </Box>
                                    <Box sx={{ marginTop: 2 }}>
                                        <TextField
                                            label="Confirm new password"
                                            value={verifyNewPassword}
                                            onChange={(e) => setVerifyNewPassword(e.target.value)}
                                            type="password"
                                            onBlur={validateNewPassword(true)}
                                        />
                                    </Box>
                                    {inputErrors.newPassword ? <span>{inputErrors.newPassword}</span> : null}
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Divider sx={{ marginBottom: '24px' }} />
                                    Your current password is required to save changes.
                                    <Box>
                                        <TextField
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Current Password"
                                            type="password"
                                        />
                                    </Box>
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Button
                                        variant="contained"
                                        disabled={
                                            saving ||
                                            loading ||
                                            hasInputErrors ||
                                            (!diffs.length && !newPassword) ||
                                            !password
                                        }
                                        onClick={() => sendUpdateRequest()}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid size={{ xs: 4 }}>
                            <Typography variant="h6">Account Information</Typography>

                            <Typography>
                                Member since <strong>{dayjs(user.date_created).format('MM/DD/YYYY')}</strong>
                            </Typography>

                            {Boolean(user.date_updated) && (
                                <Typography sx={{ marginTop: '4px' }}>
                                    Last updated <strong>{dayjs(user.date_updated).format('MM/DD/YYYY')}</strong>
                                </Typography>
                            )}

                            <Box sx={{ marginTop: '32px' }}>
                                <Typography variant="h6">Clinics</Typography>
                                {clinics.map((clinic) => (
                                    <Typography
                                        key={clinic.clinic_id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        {clinic.clinic_name}{' '}
                                        {clinic.current && (
                                            <Tooltip title="This is the clinic you're currently logged in to.">
                                                <Star
                                                    sx={{
                                                        color: '#52aeff',
                                                        height: '20px',
                                                        width: '20px',
                                                        marginLeft: '8px',
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Typography>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Box>
        </DashboardCard>
    );
};

export default MyAccount;
